import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Wallet,
  Sparkles,
  Activity,
  AlertOctagon,
  Building2,
  Eye,
  Settings,
  KeyRound,
  Power,
  ArrowRight,
} from 'lucide-react';

import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { KPI } from '@/components/ui/kpi';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Pill } from '@/components/ui/pill';
import {
  useMasterBillingStats,
  useMasterSubscriptions,
  formatBrl,
  type SubscriptionRow,
} from '@/lib/hooks/billing';
import { api } from '@/lib/api';

// ─── Rota ────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/_app/master/')({
  component: MasterDashboard,
});

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface Workspace {
  id: string;
  slug: string;
  name: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  createdAt: string;
  adminEmail: string;
  userCount: number;
}

// ─── Utilitários ─────────────────────────────────────────────────────────────

function currentMonthLabel(): string {
  return new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(new Date());
}

function workspaceStatusTone(
  status: Workspace['status'],
): 'mint' | 'warn' | 'default' {
  if (status === 'ACTIVE') return 'mint';
  if (status === 'SUSPENDED') return 'warn';
  return 'default';
}

function workspaceStatusLabel(status: Workspace['status']): string {
  return { ACTIVE: 'Ativo', SUSPENDED: 'Suspenso', ARCHIVED: 'Arquivado' }[status];
}

function healthDot(
  wsStatus: Workspace['status'],
  subStatus?: SubscriptionRow['status'],
): 'pass' | 'warn' | 'block' {
  if (wsStatus !== 'ACTIVE') return 'block';
  if (subStatus === 'PAST_DUE') return 'warn';
  if (subStatus === 'ACTIVE' || subStatus === 'TRIAL') return 'pass';
  return 'warn';
}

const dotColorMap: Record<'pass' | 'warn' | 'block', string> = {
  pass: 'bg-pass',
  warn: 'bg-warn',
  block: 'bg-block',
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function HeroInk({
  totalWorkspaces,
  activeWorkspaces,
  mrr,
  isLoading,
}: {
  totalWorkspaces: number;
  activeWorkspaces: number;
  mrr: number;
  isLoading: boolean;
}) {
  const month = currentMonthLabel();

  return (
    <Card
      variant="ink"
      className="p-7 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between"
    >
      <div className="flex flex-col gap-3 min-w-0">
        <Eyebrow className="text-on-ink opacity-70">
          Visão Master · {month}
        </Eyebrow>

        {isLoading ? (
          <div className="h-9 w-64 rounded-2 bg-white/10 animate-pulse" />
        ) : (
          <h1 className="font-display text-[30px] font-bold leading-tight tracking-[-0.02em] text-on-ink">
            {totalWorkspaces} workspaces&nbsp;·&nbsp;
            <span className="text-mint">{activeWorkspaces} ativos</span>
          </h1>
        )}

        <div className="flex flex-col gap-1">
          {isLoading ? (
            <div className="h-5 w-40 rounded-2 bg-white/10 animate-pulse" />
          ) : (
            <span className="font-mono text-[15px] tabular-nums text-on-ink font-semibold">
              MRR:&nbsp;{formatBrl(mrr)}
            </span>
          )}
          <span className="font-mono text-[11px] text-on-ink opacity-50">
            Steel sessions: n/d
          </span>
        </div>
      </div>

      <div className="flex flex-row sm:flex-col gap-2 shrink-0">
        <Button variant="ghost" size="sm" className="text-on-ink/80 hover:text-on-ink hover:bg-white/10" asChild>
          <Link to="/master/workspaces">
            Gerenciar workspaces
          </Link>
        </Button>
        <Button variant="ghost" size="sm" className="text-on-ink/80 hover:text-on-ink hover:bg-white/10" asChild>
          <Link to="/master/billing">
            Billing
          </Link>
        </Button>
      </div>
    </Card>
  );
}

function KpiStrip({ mrr, isLoading }: { mrr: number; isLoading: boolean }) {
  const pendingValue = (
    <span className="text-[11px] font-mono text-fg-muted tracking-wide">
      endpoint pendente
    </span>
  ) as unknown as string;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      <KPI
        label="MRR"
        value={isLoading ? '…' : formatBrl(mrr)}
        icon={Wallet}
        tone="mint"
      />
      <KPI
        label="Custos IA"
        value="—"
        delta="endpoint pendente"
        icon={Sparkles}
        tone="clay"
      />
      <KPI
        label="Tokens (M)"
        value="—"
        delta="endpoint pendente"
        icon={Activity}
        tone="lilac"
      />
      <KPI
        label="Erros"
        value="—"
        delta="endpoint pendente"
        icon={AlertOctagon}
        tone="default"
      />
    </div>
  );
}

function WorkspaceRow({
  ws,
  subscription,
  onImpersonate,
  onToggleSuspend,
}: {
  ws: Workspace;
  subscription: SubscriptionRow | undefined;
  onImpersonate: (id: string, name: string) => void;
  onToggleSuspend: (ws: Workspace) => void;
}) {
  const health = healthDot(ws.status, subscription?.status);

  return (
    <tr className="border-b border-card-stroke last:border-0 hover:bg-card-soft/60 transition-colors">
      {/* Nome */}
      <td className="py-3 pl-4 pr-2">
        <div className="flex items-center gap-2.5">
          <div className="h-7 w-7 rounded-2 bg-card-soft grid place-items-center shrink-0">
            <Building2 size={14} className="text-fg-muted" strokeWidth={1.6} />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-fg truncate">{ws.name}</p>
            <p className="text-[11px] font-mono text-fg-muted truncate">{ws.adminEmail}</p>
          </div>
        </div>
      </td>

      {/* Plano */}
      <td className="py-3 px-2 hidden md:table-cell">
        {subscription ? (
          <span className="font-mono text-[11px] font-semibold text-fg">
            {subscription.plan}
          </span>
        ) : (
          <span className="text-[11px] text-fg-muted">—</span>
        )}
      </td>

      {/* Status */}
      <td className="py-3 px-2 hidden sm:table-cell">
        <Pill tone={workspaceStatusTone(ws.status)} dot>
          {workspaceStatusLabel(ws.status)}
        </Pill>
      </td>

      {/* Saúde */}
      <td className="py-3 px-2 hidden lg:table-cell">
        <span className="flex items-center gap-1.5">
          <span
            className={`h-2 w-2 rounded-full ${dotColorMap[health]}`}
            aria-label={health}
          />
          <span className="text-[11px] font-mono text-fg-muted capitalize">{health}</span>
        </span>
      </td>

      {/* Última prest */}
      <td className="py-3 px-2 hidden xl:table-cell">
        <span className="text-[11px] text-fg-muted font-mono">—</span>
      </td>

      {/* Ações */}
      <td className="py-3 pl-2 pr-4">
        <div className="flex items-center gap-1 justify-end">
          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            title={`Ver workspace ${ws.name}`}
            aria-label={`Ver workspace ${ws.name}`}
            asChild
          >
            <Link to="/master/workspaces">
              <Eye size={14} strokeWidth={1.6} />
            </Link>
          </Button>

          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            title={`Editar ${ws.name}`}
            aria-label={`Editar ${ws.name}`}
            asChild
          >
            <Link to="/master/workspaces">
              <Settings size={14} strokeWidth={1.6} />
            </Link>
          </Button>

          <Button
            variant="ghost"
            className="h-8 w-8 p-0"
            title={`Impersonar ${ws.name}`}
            aria-label={`Impersonar ${ws.name}`}
            onClick={() => onImpersonate(ws.id, ws.name)}
          >
            <KeyRound size={14} strokeWidth={1.6} />
          </Button>

          <Button
            variant="ghost"
            className={`h-8 w-8 p-0 ${ws.status === 'SUSPENDED' ? 'text-pass' : 'text-fg-muted hover:text-warn'}`}
            title={ws.status === 'SUSPENDED' ? `Reativar ${ws.name}` : `Suspender ${ws.name}`}
            aria-label={ws.status === 'SUSPENDED' ? `Reativar ${ws.name}` : `Suspender ${ws.name}`}
            onClick={() => onToggleSuspend(ws)}
          >
            <Power size={14} strokeWidth={1.6} />
          </Button>
        </div>
      </td>
    </tr>
  );
}

// ─── Tabela skeleton ──────────────────────────────────────────────────────────

function TableSkeleton() {
  return (
    <tbody>
      {Array.from({ length: 5 }).map((_, i) => (
        <tr key={i} className="border-b border-card-stroke last:border-0">
          <td className="py-3 pl-4 pr-2">
            <div className="flex items-center gap-2.5">
              <div className="h-7 w-7 rounded-2 bg-card-soft animate-pulse" />
              <div className="flex flex-col gap-1">
                <div className="h-3.5 w-32 rounded-pill bg-card-soft animate-pulse" />
                <div className="h-2.5 w-24 rounded-pill bg-card-soft animate-pulse" />
              </div>
            </div>
          </td>
          <td className="py-3 px-2 hidden md:table-cell">
            <div className="h-3 w-12 rounded-pill bg-card-soft animate-pulse" />
          </td>
          <td className="py-3 px-2 hidden sm:table-cell">
            <div className="h-5 w-16 rounded-pill bg-card-soft animate-pulse" />
          </td>
          <td className="py-3 px-2 hidden lg:table-cell">
            <div className="h-3 w-10 rounded-pill bg-card-soft animate-pulse" />
          </td>
          <td className="py-3 px-2 hidden xl:table-cell">
            <div className="h-3 w-14 rounded-pill bg-card-soft animate-pulse" />
          </td>
          <td className="py-3 pl-2 pr-4">
            <div className="flex gap-1 justify-end">
              {[1, 2, 3, 4].map((j) => (
                <div key={j} className="h-8 w-8 rounded-pill bg-card-soft animate-pulse" />
              ))}
            </div>
          </td>
        </tr>
      ))}
    </tbody>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

function MasterDashboard() {
  const qc = useQueryClient();

  const { data: billingStats, isLoading: statsLoading } = useMasterBillingStats();
  const { data: subscriptions } = useMasterSubscriptions();

  const { data: workspacesData, isLoading: wsLoading } = useQuery({
    queryKey: ['master', 'workspaces'],
    queryFn: () => api.get<{ items: Workspace[] }>('/v1/master/workspaces'),
  });

  const stats = billingStats ?? { total: 0, active: 0, pending: 0, cancelled: 0, mrr: 0 };
  const workspaces = (workspacesData?.items ?? []).slice(0, 8);
  const subMap = new Map<string, SubscriptionRow>(
    (subscriptions?.items ?? []).map((s) => [s.workspaceId, s]),
  );

  async function handleImpersonate(id: string, name: string) {
    try {
      await api.post<{ token: string }>(`/v1/master/workspaces/${id}/impersonate`, {});
      qc.invalidateQueries({ queryKey: ['auth'] });
      toast.success(`Impersonando ${name}`);
    } catch {
      toast.error(`Não conseguimos impersonar ${name}. Tente novamente.`);
    }
  }

  async function handleToggleSuspend(ws: Workspace) {
    const endpoint =
      ws.status === 'SUSPENDED'
        ? `/v1/master/workspaces/${ws.id}/reactivate`
        : `/v1/master/workspaces/${ws.id}/suspend`;
    const action = ws.status === 'SUSPENDED' ? 'reativado' : 'suspenso';

    try {
      await api.post<{ ok: boolean }>(endpoint, {});
      qc.invalidateQueries({ queryKey: ['master', 'workspaces'] });
      toast.success(`Workspace ${ws.name} ${action} com sucesso.`);
    } catch {
      toast.error(`Não conseguimos atualizar ${ws.name}. Tente novamente.`);
    }
  }

  return (
    <div className="flex flex-col gap-5 pb-10">
      {/* Hero */}
      <HeroInk
        totalWorkspaces={stats.total}
        activeWorkspaces={stats.active}
        mrr={stats.mrr}
        isLoading={statsLoading}
      />

      {/* KPI strip */}
      <KpiStrip mrr={stats.mrr} isLoading={statsLoading} />

      {/* Tabela de workspaces */}
      <Card variant="default" className="overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-5 pb-3 gap-3">
          <div className="flex flex-col gap-0.5">
            <Eyebrow>Workspaces</Eyebrow>
            <h2 className="font-display text-[18px] font-semibold tracking-tight text-fg">
              Visão geral
            </h2>
          </div>
          <Button variant="ghost" size="sm" className="gap-1.5 text-mint-deep" asChild>
            <Link to="/master/workspaces">
              Ver todos <ArrowRight size={13} strokeWidth={1.8} />
            </Link>
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-label="Tabela de workspaces">
            <thead>
              <tr className="border-b border-card-stroke bg-card-soft/60">
                <th className="py-2.5 pl-4 pr-2 text-left font-mono text-[10px] tracking-[0.08em] uppercase text-fg-muted font-semibold">
                  Nome
                </th>
                <th className="py-2.5 px-2 text-left font-mono text-[10px] tracking-[0.08em] uppercase text-fg-muted font-semibold hidden md:table-cell">
                  Plano
                </th>
                <th className="py-2.5 px-2 text-left font-mono text-[10px] tracking-[0.08em] uppercase text-fg-muted font-semibold hidden sm:table-cell">
                  Status
                </th>
                <th className="py-2.5 px-2 text-left font-mono text-[10px] tracking-[0.08em] uppercase text-fg-muted font-semibold hidden lg:table-cell">
                  Saúde
                </th>
                <th className="py-2.5 px-2 text-left font-mono text-[10px] tracking-[0.08em] uppercase text-fg-muted font-semibold hidden xl:table-cell">
                  Última prest
                </th>
                <th className="py-2.5 pl-2 pr-4 text-right font-mono text-[10px] tracking-[0.08em] uppercase text-fg-muted font-semibold">
                  Ações
                </th>
              </tr>
            </thead>

            {wsLoading ? (
              <TableSkeleton />
            ) : workspaces.length === 0 ? (
              <tbody>
                <tr>
                  <td
                    colSpan={6}
                    className="py-12 text-center text-sm text-fg-muted"
                  >
                    <div className="flex flex-col items-center gap-3">
                      <Building2 size={32} className="text-fg-muted opacity-40" strokeWidth={1.4} />
                      <p>Nenhum workspace encontrado ainda.</p>
                      <Button variant="primary" size="sm" asChild>
                        <Link to="/master/workspaces">Criar primeiro workspace</Link>
                      </Button>
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {workspaces.map((ws) => (
                  <WorkspaceRow
                    key={ws.id}
                    ws={ws}
                    subscription={subMap.get(ws.id)}
                    onImpersonate={handleImpersonate}
                    onToggleSuspend={handleToggleSuspend}
                  />
                ))}
              </tbody>
            )}
          </table>
        </div>

        {!wsLoading && workspaces.length > 0 && (
          <div className="px-4 py-3 border-t border-card-stroke flex justify-end">
            <Button variant="ghost" size="sm" className="gap-1.5 text-mint-deep" asChild>
              <Link to="/master/workspaces">
                Ver todos os workspaces <ArrowRight size={13} strokeWidth={1.8} />
              </Link>
            </Button>
          </div>
        )}
      </Card>

      {/* Chart placeholder */}
      <Card
        variant="default"
        className="p-8 grid place-items-center min-h-[160px] text-sm text-fg-muted"
      >
        Gráfico de uso/custo por workspace virá quando o endpoint /v1/master/usage estiver pronto.
      </Card>
    </div>
  );
}
